import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// Claro, escuro, ou o que o sistema pedir.
//
// Três estados, não dois: "sistema" é o padrão e é diferente de "claro". Quem
// nunca tocou no assunto acompanha o aparelho — e no celular isso quer dizer
// acompanhar o modo noturno automático. Guardar "claro" para quem não escolheu
// seria decidir por ele.
//
// A escolha vive em localStorage, não no perfil: é preferência de dispositivo (o
// mesmo aluno quer escuro no celular à noite e claro no desktop de dia), e
// gravá-la no banco exigiria escrita do cliente, que a arquitetura do hub não
// permite.
export type Tema = "claro" | "escuro" | "sistema";

const CHAVE = "cf-tema";

type EstadoTema = {
  tema: Tema;
  /** O que está de fato na tela agora — "sistema" já resolvido. */
  efetivo: "claro" | "escuro";
  definir: (t: Tema) => void;
};

const Ctx = createContext<EstadoTema>({ tema: "sistema", efetivo: "claro", definir: () => {} });

function lerPreferencia(): Tema {
  try {
    const v = localStorage.getItem(CHAVE);
    if (v === "claro" || v === "escuro" || v === "sistema") return v;
  } catch {
    // localStorage bloqueado (navegação privada em alguns navegadores). Sem
    // preferência salva o hub segue o sistema, que é o padrão.
  }
  return "sistema";
}

function sistemaEscuro(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

export function TemaProvider({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(lerPreferencia);
  const [escuroDoSistema, setEscuroDoSistema] = useState(sistemaEscuro);

  // Quem está em "sistema" acompanha a troca sem recarregar a página — inclui o
  // caso de o aparelho virar para o modo noturno com o hub aberto.
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const aoMudar = (e: MediaQueryListEvent) => setEscuroDoSistema(e.matches);
    mq.addEventListener("change", aoMudar);
    return () => mq.removeEventListener("change", aoMudar);
  }, []);

  const efetivo: "claro" | "escuro" =
    tema === "sistema" ? (escuroDoSistema ? "escuro" : "claro") : tema;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", efetivo === "escuro");
    // A cor da barra do navegador no celular acompanha: sem isto o topo do
    // Chrome fica claro sobre um app escuro, e a emenda salta aos olhos.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", efetivo === "escuro" ? "#101a30" : "#102850");
  }, [efetivo]);

  function definir(t: Tema) {
    setTema(t);
    try {
      localStorage.setItem(CHAVE, t);
    } catch {
      // Sem persistência a escolha vale só nesta sessão — melhor que quebrar.
    }
  }

  return <Ctx.Provider value={{ tema, efetivo, definir }}>{children}</Ctx.Provider>;
}

export function useTema() {
  return useContext(Ctx);
}
